import { JsonView, darkStyles, defaultStyles } from 'react-json-view-lite'
import 'react-json-view-lite/dist/index.css'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useRunResults } from '@/hooks/use-runs'
import type { RunDetail } from '@/api/types'

interface ResultsTabProps {
  run: RunDetail
}

export function ResultsTab({ run }: ResultsTabProps) {
  // Fetch results (separate endpoint from run detail)
  const { data: results, isLoading, error } = useRunResults(run.fileId)

  // Use results from prop or fetched data
  const displayResults = run.results || results

  // Check for dark mode
  const isDarkMode = document.documentElement.classList.contains('dark')

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4 animate-pulse">
            <div className="h-4 w-full bg-muted rounded" />
            <div className="h-4 w-3/4 bg-muted rounded" />
            <div className="h-4 w-1/2 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">
            Error loading results: {error.message}
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!displayResults || Object.keys(displayResults).length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">
            {run.status === 'processing'
              ? 'Results will appear when processing completes.'
              : run.status === 'failed'
              ? 'No results available - run failed.'
              : 'No extraction results available for this run.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  // Extract summary stats
  const rates = (displayResults as { rates?: unknown[] }).rates
  const surcharges = (displayResults as { surcharges?: unknown[] }).surcharges

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Extraction Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {rates && (
              <div className="flex items-center gap-2">
                <Badge variant="outline">Rates</Badge>
                <span className="font-medium">{rates.length}</span>
              </div>
            )}
            {surcharges && (
              <div className="flex items-center gap-2">
                <Badge variant="outline">Surcharges</Badge>
                <span className="font-medium">{surcharges.length}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Full JSON */}
      <Card>
        <CardHeader>
          <CardTitle>Extracted Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border bg-muted/50 p-4 overflow-auto max-h-[600px]">
            <JsonView
              data={displayResults}
              shouldExpandNode={(level) => level < 2}
              style={isDarkMode ? darkStyles : defaultStyles}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
